import React, { useState } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Menu,
  MenuItem
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  CalendarToday as AppointmentsIcon,
  Payment as PaymentIcon,
  Assessment as ReportsIcon,
  MedicalServices as DoctorsIcon,
  RoomService as ServicesIcon,
  Receipt as InvoicesIcon,
  Science as TestsIcon,
  Hotel as RoomsIcon,
  ExitToApp as DischargeIcon,
  Person as ProfileIcon,
  ExitToApp as LogoutIcon,
  AccountCircle
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppContext } from '../contexts/AppContext';

const drawerWidth = 280;

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [adminName, setAdminName] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, state } = useAppContext();

  // Load admin name from localStorage
  React.useEffect(() => {
    const loadAdminName = () => {
      try {
        const adminDataStr = localStorage.getItem('adminData');
        const adminEmail = localStorage.getItem('adminEmail');
        
        console.log('Layout loading admin name from:', { adminDataStr, adminEmail });
        
        if (adminDataStr) {
          const adminData = JSON.parse(adminDataStr);
          setAdminName(adminData.fullName || adminData.email || adminEmail || 'Administrator');
        } else if (adminEmail) {
          // Fallback to email if adminData not available
          const emailName = adminEmail.split('@')[0].replace(/[._-]/g, ' ')
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
          setAdminName(emailName);
        } else {
          setAdminName('Administrator');
        }
      } catch (error) {
        console.error('Error loading admin name:', error);
        setAdminName('Administrator');
      }
    };

    loadAdminName();
    
    // Listen for custom admin data update event
    const handleAdminUpdate = () => {
      console.log('Layout detected admin data update');
      loadAdminName();
    };
    
    window.addEventListener('adminDataUpdated', handleAdminUpdate);
    window.addEventListener('storage', handleAdminUpdate); // Keep for cross-tab sync
    
    return () => {
      window.removeEventListener('adminDataUpdated', handleAdminUpdate);
      window.removeEventListener('storage', handleAdminUpdate);
    };
  }, [location.pathname]); // Reload when navigating

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleClose();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Patients', icon: <PeopleIcon />, path: '/patients' },
    { text: 'Appointments', icon: <AppointmentsIcon />, path: '/appointments' },
    { text: 'Payments', icon: <PaymentIcon />, path: '/payments' },
    { text: 'Reports', icon: <ReportsIcon />, path: '/reports' },
    { text: 'Doctors', icon: <DoctorsIcon />, path: '/doctors' },
    { text: 'Services', icon: <ServicesIcon />, path: '/services' },
    { text: 'Invoices Management', icon: <InvoicesIcon />, path: '/invoices' },
    { text: 'Tests', icon: <TestsIcon />, path: '/tests' },
    { text: 'Rooms', icon: <RoomsIcon />, path: '/rooms' },
    { text: 'Discharge', icon: <DischargeIcon />, path: '/discharge' },
  ];

  const isSelected = (path: string) => location.pathname === path;

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      backgroundColor: '#4A5568', 
      color: 'white',
      display: 'flex',
      flexDirection: 'column',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{ 
        p: 3, 
        backgroundColor: '#2D3748',
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        minHeight: '80px',
        flexShrink: 0
      }}>
        <Box
          component="img"
          src="/images/logo.png"
          alt="KBR Life Care Logo"
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1,
            objectFit: 'contain',
            flexShrink: 0
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 'bold', 
            fontSize: { xs: '14px', sm: '16px' },
            lineHeight: 1.2,
            color: 'white !important'
          }}>
            KBR LIFE CARE
          </Typography>
          <Typography variant="body2" sx={{ 
            color: '#A0AEC0 !important', 
            fontSize: { xs: '11px', sm: '12px' },
            lineHeight: 1.2
          }}>
            HOSPITALS
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ borderColor: '#2D3748', flexShrink: 0 }} />

      {/* Menu Items */}
      <List sx={{ 
        px: 1, 
        py: 2, 
        flex: 1, 
        overflow: 'auto',
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#2D3748',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#4A90E2',
          borderRadius: '2px',
        }
      }}>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              onClick={() => navigate(item.path)}
              sx={{
                borderRadius: 2,
                mx: 1,
                backgroundColor: isSelected(item.path) ? '#4A90E2 !important' : 'transparent',
                '&:hover': {
                  backgroundColor: isSelected(item.path) ? '#3A7BD5 !important' : 'rgba(74, 144, 226, 0.15) !important',
                },
                color: 'white !important',
                py: { xs: 1.2, sm: 1.5 },
                px: { xs: 1.5, sm: 2 },
                minHeight: { xs: '44px', sm: '48px' },
                transition: 'all 0.2s ease-in-out',
                '&:focus': {
                  backgroundColor: isSelected(item.path) ? '#4A90E2 !important' : 'rgba(74, 144, 226, 0.15) !important',
                }
              }}
            >
              <ListItemIcon sx={{ 
                color: 'white !important', 
                minWidth: { xs: '32px', sm: '36px' },
                '& svg': { 
                  fontSize: { xs: '18px', sm: '20px' },
                  color: 'white !important'
                }
              }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText 
                primary={item.text}
                primaryTypographyProps={{
                  fontSize: { xs: '13px', sm: '14px' },
                  fontWeight: isSelected(item.path) ? 600 : 400,
                  color: 'white !important'
                }}
                sx={{
                  '& .MuiListItemText-primary': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                  }
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      <Divider sx={{ borderColor: '#2D3748', mx: 2, flexShrink: 0 }} />

      {/* System Administrator Profile */}
      <Box sx={{ px: 1, py: 1, flexShrink: 0 }}>
        <ListItemButton
          onClick={() => navigate('/profile')}
          sx={{
            borderRadius: 2,
            mx: 1,
            backgroundColor: isSelected('/profile') ? '#4A90E2 !important' : 'transparent',
            '&:hover': {
              backgroundColor: isSelected('/profile') ? '#3A7BD5 !important' : 'rgba(74, 144, 226, 0.15) !important',
            },
            color: 'white !important',
            py: { xs: 1.2, sm: 1.5 },
            px: { xs: 1.5, sm: 2 },
            minHeight: '48px',
            transition: 'all 0.2s ease-in-out'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', minWidth: 0 }}>
            <ListItemIcon sx={{ 
              color: 'white !important', 
              minWidth: { xs: '32px', sm: '36px' },
              '& svg': { 
                fontSize: { xs: '18px', sm: '20px' },
                color: 'white !important'
              }
            }}>
              <ProfileIcon />
            </ListItemIcon>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography 
                variant="body2" 
                noWrap
                sx={{ 
                  fontWeight: isSelected('/profile') ? 600 : 500, 
                  fontSize: { xs: '13px', sm: '14px' },
                  color: 'white !important'
                }}
              >
                {adminName || 'Administrator'}
              </Typography>
              <Typography variant="caption" sx={{ 
                color: '#A0AEC0 !important', 
                fontSize: { xs: '11px', sm: '12px' },
                lineHeight: 1.2
              }}>
                Admin
              </Typography>
            </Box>
          </Box>
        </ListItemButton>
      </Box>

      {/* Logout Button */}
      <Box sx={{ px: 1, pb: 2, flexShrink: 0 }}>
        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            mx: 1,
            backgroundColor: '#EF4444 !important',
            color: 'white !important',
            py: { xs: 1.2, sm: 1.5 },
            px: { xs: 1.5, sm: 2 },
            minHeight: '48px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              backgroundColor: '#DC2626 !important',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 8px rgba(239, 68, 68, 0.3)'
            }
          }}
        >
          <ListItemIcon sx={{ 
            color: 'white !important', 
            minWidth: { xs: '32px', sm: '36px' },
            '& svg': { 
              fontSize: { xs: '18px', sm: '20px' },
              color: 'white !important'
            }
          }}>
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText 
            primary="Logout"
            primaryTypographyProps={{
              fontSize: { xs: '13px', sm: '14px' },
              fontWeight: 600,
              color: 'white !important'
            }}
          />
        </ListItemButton>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: 'white',
          color: '#1a202c',
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
          borderBottom: '1px solid #e2e8f0'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }} />
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              border: 'none',
              backgroundColor: '#4A5568'
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              border: 'none',
              backgroundColor: '#4A5568',
              position: 'fixed',
              height: '100vh',
              overflow: 'hidden'
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: '#f7fafc',
          minHeight: '100vh'
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;